import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { mascaraTelefone } from '@/pages/inscricao/utils';
import FundoRifa from './components/FundoRifa';
import PassoOferta from './components/PassoOferta';
import PassoPix from './components/PassoPix';
import PassoPronto from './components/PassoPronto';
import EstadoErro from './components/EstadoErro';
import type { ConsultaCompra, PixRifa, TalaoPublico } from './types';

const INTERVALO_POLLING_MS = 5000;
const LIMITE_CONSULTAS_MS = 35 * 60 * 1000;

type Etapa = 'carregando' | 'oferta' | 'pix' | 'pronto' | 'expirado';

const CHAVE_LOCAL = (token: string) => `rifa_${token}`;

function iconeParaErro(m: string): string {
  if (m.includes('não encontrado')) return 'ri-search-line';
  if (m.includes('cancelado')) return 'ri-close-circle-line';
  if (m.includes('encerrou')) return 'ri-time-line';
  return 'ri-error-warning-line';
}

async function carregarTalaoPublico(token: string): Promise<TalaoPublico | null> {
  const { data, error } = await supabase.rpc('talao_publico', { p_token: token });
  if (error) throw error;
  return (data as TalaoPublico) ?? null;
}

async function consultarCompra(referencia: string): Promise<ConsultaCompra | null> {
  const { data, error } = await supabase.rpc('consultar_compra', {
    p_referencia: referencia,
  });
  if (error) throw error;
  return (data as ConsultaCompra) ?? null;
}

/** Skeleton de vidro enquanto talão carrega */
function SkeletonCarregando({ imagemUrl }: { imagemUrl?: string | null }) {
  return (
    <FundoRifa imagemUrl={imagemUrl}>
      <div className="mx-auto w-full max-w-[560px] animate-pulse px-4 py-6">
        {/* Título fake */}
        <div className="mb-5 text-center">
          <div className="mx-auto mb-2 h-9 w-2/3 rounded-2xl bg-white/30" />
          <div className="mx-auto h-5 w-1/2 rounded-xl bg-white/20" />
        </div>
        {/* Card prêmio */}
        <div className="mb-3 rounded-3xl bg-white/25 backdrop-blur-md p-6 shadow-xl">
          <div className="h-44 w-full rounded-2xl bg-white/30" />
        </div>
        {/* Card quantidade */}
        <div className="mb-3 rounded-3xl bg-white/25 backdrop-blur-md p-6 shadow-xl">
          <div className="h-20 w-full rounded-2xl bg-white/30" />
        </div>
        {/* Card dados */}
        <div className="mb-3 rounded-3xl bg-white/25 backdrop-blur-md p-6 shadow-xl">
          <div className="mb-3 h-12 w-full rounded-2xl bg-white/30" />
          <div className="h-12 w-full rounded-2xl bg-white/30" />
        </div>
      </div>
    </FundoRifa>
  );
}

export default function TalaoPage() {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();

  const [carregando, setCarregando] = useState(true);
  const [erroTalao, setErroTalao] = useState<string | null>(null);
  const [erroCarregar, setErroCarregar] = useState<string | null>(null);
  const [talao, setTalao] = useState<TalaoPublico | null>(null);

  const [etapa, setEtapa] = useState<Etapa>('carregando');

  const [chave, setChave] = useState(() => crypto.randomUUID());

  // Passo 1 — oferta
  const [quantidade, setQuantidade] = useState(1);
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [erroPix, setErroPix] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);

  // Passo 2 — pix
  const [pix, setPix] = useState<PixRifa | null>(null);
  const [referencia, setReferencia] = useState('');
  const [expiraEm, setExpiraEm] = useState<string | null>(null);
  const [numerosComprados, setNumerosComprados] = useState<number[]>([]);
  const [nomeComprador, setNomeComprador] = useState<string | null>(null);
  const [semQR, setSemQR] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const [semInternet, setSemInternet] = useState(false);
  const [tempoEsgotado, setTempoEsgotado] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [pollKey, setPollKey] = useState(0);
  const [capaImagem, setCapaImagem] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;

    async function iniciar() {
      setCarregando(true);
      setErroCarregar(null);
      setErroTalao(null);
      try {
        const t = await carregarTalaoPublico(token ?? '');
        if (!ativo) return;
        if (!t || t.ok === false) {
          setErroTalao(t?.erro || 'Não foi possível carregar. Tente novamente.');
          return;
        }
        setTalao(t);

        // ─── ?c={referencia} — reabertura por link em qualquer aparelho ───
        const cParam = searchParams.get('c');
        if (cParam) {
          try {
            const s = await consultarCompra(cParam);
            if (ativo && s?.ok && s.situacao === 'pago') {
              setNumerosComprados(s.numeros || []);
              setNomeComprador(s.comprador ?? null);
              setReferencia(cParam);
              setEtapa('pronto');
              return;
            }
            if (ativo && s?.ok && s.situacao === 'aguardando') {
              setNumerosComprados(s.numeros || []);
              setReferencia(cParam);
              setExpiraEm(s.expira_em ?? null);
              setSemQR(true);
              setEtapa('pix');
              return;
            }
          } catch {
            // ignora e segue
          }
        }

        // ─── Reabertura via localStorage ───
        const ref = localStorage.getItem(CHAVE_LOCAL(token ?? ''));
        if (ref) {
          try {
            const s = await consultarCompra(ref);
            if (ativo && s?.ok && s.situacao === 'pago') {
              setNumerosComprados(s.numeros || []);
              setNomeComprador(s.comprador ?? null);
              setReferencia(ref);
              setEtapa('pronto');
              return;
            }
            if (ativo && s?.ok && s.situacao === 'aguardando') {
              setNumerosComprados(s.numeros || []);
              setReferencia(ref);
              setExpiraEm(s.expira_em ?? null);
              setSemQR(true);
              setEtapa('pix');
              return;
            }
          } catch {
            // cai no fluxo normal
          }
          localStorage.removeItem(CHAVE_LOCAL(token ?? ''));
        }

        if (ativo) setEtapa('oferta');
      } catch (e) {
        if (ativo) {
          setErroCarregar(
            e instanceof Error ? e.message : 'Não foi possível carregar. Tente novamente.',
          );
        }
      } finally {
        if (ativo) setCarregando(false);
      }
    }

    iniciar();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Capa da rifa (capa própria › foto do 1º prêmio › nada) para o fundo.
  useEffect(() => {
    if (!talao) return;
    let ativo = true;
    (async () => {
      const { data } = await supabase.rpc('capa_da_rifa', { p_rifa: talao.rifa_id });
      if (ativo && data) setCapaImagem((data as { imagem?: string | null }).imagem ?? null);
    })();
    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [talao?.rifa_id]);

  async function pagarComPix() {
    setGerando(true);
    setErroPix(null);
    try {
      const { data } = await supabase.functions.invoke('criar-pix-rifa', {
        body: {
          codigo: token,
          quantidade,
          nome: nome.trim(),
          telefone: telefone.replace(/\D/g, ''),
          chave,
        },
      });

      const resposta = (data ?? {}) as PixRifa;

      if (resposta.ok === false) {
        try {
          const t = await carregarTalaoPublico(token ?? '');
          if (t && t.ok !== false) setTalao(t);
        } catch { /* mantém */ }

        setErroPix(
          typeof resposta.erro === 'string' && resposta.erro
            ? resposta.erro
            : 'Não foi possível. Tente de novo.',
        );

        if (resposta.ainda_tem && Number.isFinite(resposta.ainda_tem) && resposta.ainda_tem >= 1) {
          const limiteAtual = talao ? Math.max(1, talao.max_por_compra) : resposta.ainda_tem;
          setQuantidade(Math.max(1, Math.min(resposta.ainda_tem, limiteAtual)));
        }
        return;
      }

      const numeros = Array.isArray(resposta.numeros) ? resposta.numeros : [];
      const ref = resposta.referencia || '';
      setNumerosComprados(numeros);
      setNomeComprador(nome.trim() || null);
      setPix(resposta);
      setReferencia(ref);
      setSemQR(false);
      setExpiraEm(null);
      if (ref) localStorage.setItem(CHAVE_LOCAL(token ?? ''), ref);
      setEtapa('pix');
    } catch (e) {
      setErroPix(e instanceof Error ? e.message : 'Não foi possível. Tente de novo.');
    } finally {
      setGerando(false);
    }
  }

  // Polling: pix → pronto
  useEffect(() => {
    if (etapa !== 'pix' || !referencia) return;

    setSemInternet(false);
    setTempoEsgotado(false);

    const inicio = Date.now();
    let falhas = 0;
    let intervalo = 0;

    async function checar() {
      if (Date.now() - inicio > LIMITE_CONSULTAS_MS) {
        setTempoEsgotado(true);
        window.clearInterval(intervalo);
        return;
      }
      try {
        const s = await consultarCompra(referencia);
        falhas = 0;
        setSemInternet(false);
        if (s?.ok) {
          if (s.situacao === 'pago') {
            setNumerosComprados(s.numeros || []);
            setNomeComprador(s.comprador ?? nomeComprador);
            setEtapa('pronto');
            window.clearInterval(intervalo);
          } else if (s.situacao === 'expirado') {
            localStorage.removeItem(CHAVE_LOCAL(token ?? ''));
            setEtapa('expirado');
            window.clearInterval(intervalo);
          }
        }
      } catch {
        falhas += 1;
        if (falhas >= 10) setSemInternet(true);
      }
    }

    checar();
    intervalo = window.setInterval(checar, INTERVALO_POLLING_MS);
    return () => window.clearInterval(intervalo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapa, referencia, pollKey, token]);

  async function verificarAgora() {
    if (!referencia) return;
    setVerificando(true);
    try {
      const s = await consultarCompra(referencia);
      if (s?.ok && s.situacao === 'pago') {
        setNumerosComprados(s.numeros || []);
        setNomeComprador(s.comprador ?? nomeComprador);
        setEtapa('pronto');
        return;
      }
      if (s?.ok && s.situacao === 'expirado') {
        localStorage.removeItem(CHAVE_LOCAL(token ?? ''));
        setEtapa('expirado');
        return;
      }
      setSemInternet(false);
      setTempoEsgotado(false);
      setPollKey((k) => k + 1);
    } catch {
      setSemInternet(false);
      setTempoEsgotado(false);
      setPollKey((k) => k + 1);
    } finally {
      setVerificando(false);
    }
  }

  async function copiarCodigo() {
    if (!pix?.pix_copia_e_cola) return;
    try {
      await navigator.clipboard.writeText(pix.pix_copia_e_cola);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch { /* não bloqueia */ }
  }

  function irParaOferta() {
    setChave(crypto.randomUUID());
    setQuantidade(1);
    setNome('');
    setTelefone('');
    setErroPix(null);
    setPix(null);
    setReferencia('');
    setExpiraEm(null);
    setNumerosComprados([]);
    setNomeComprador(null);
    setSemQR(false);
    setSemInternet(false);
    setTempoEsgotado(false);
    setEtapa('oferta');
    carregarTalaoPublico(token ?? '').then((t) => {
      if (t && t.ok !== false) setTalao(t);
    });
  }

  // ── Estados de carregamento / erro ──

  const imagemFundo = capaImagem;

  if (carregando) {
    return <SkeletonCarregando imagemUrl={imagemFundo} />;
  }

  if (erroCarregar) {
    return (
      <EstadoErro
        mensagem={erroCarregar}
        icone="ri-error-warning-line"
        aoRecarregar={() => window.location.reload()}
      />
    );
  }

  if (erroTalao) {
    return <EstadoErro mensagem={erroTalao} icone={iconeParaErro(erroTalao)} />;
  }

  if (!talao) return null;

  // ── PRONTO ──
  if (etapa === 'pronto') {
    return (
      <PassoPronto
        numeros={numerosComprados}
        talao={talao}
        nomeComprador={nomeComprador}
        referencia={referencia}
        aoComprarMais={irParaOferta}
        imagemFundo={imagemFundo}
      />
    );
  }

  // ── EXPIRADO ──
  if (etapa === 'expirado') {
    return (
      <FundoRifa imagemUrl={imagemFundo}>
        <div className="flex min-h-screen items-center justify-center px-4 py-8">
          <div className="w-full max-w-[420px] overflow-hidden rounded-3xl bg-white/85 shadow-xl backdrop-blur-md">
            <div className="p-6 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-time-line text-2xl" />
              </span>
              <p className="mt-4 text-base text-foreground-800">
                O tempo acabou e os números voltaram para o talão. Se você pagou, fale com quem te
                vendeu — o pagamento está registrado.
              </p>
              <button
                type="button"
                onClick={irParaOferta}
                className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-2xl bg-gradient-to-r from-primary-500 to-primary-600 px-6 py-4 text-base font-bold text-white shadow-lg shadow-primary-500/30 transition-all hover:from-primary-600 hover:to-primary-700"
              >
                Tentar de novo
              </button>
            </div>
          </div>
        </div>
      </FundoRifa>
    );
  }

  // ── PIX ──
  if (etapa === 'pix') {
    return (
      <PassoPix
        numeros={numerosComprados}
        pix={pix}
        semQR={semQR}
        copiado={copiado}
        aoCopiar={copiarCodigo}
        semInternet={semInternet}
        tempoEsgotado={tempoEsgotado}
        verificando={verificando}
        aoVerificar={verificarAgora}
        talao={talao}
        expiraEm={expiraEm}
        imagemFundo={imagemFundo}
      />
    );
  }

  // ── ESGOTADO ──
  if (talao.restam === 0) {
    return (
      <FundoRifa imagemUrl={imagemFundo}>
        <div className="flex min-h-screen items-center justify-center px-4 py-8">
          <div className="w-full max-w-[420px] overflow-hidden rounded-3xl bg-white/85 shadow-xl backdrop-blur-md">
            <div className="p-6 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-secondary-100 text-secondary-700">
                <i className="ri-ticket-2-line text-2xl" />
              </span>
              <p className="mt-4 text-base text-foreground-800">
                Este talão já vendeu todos os números. Peça outro a quem está vendendo.
              </p>
            </div>
          </div>
        </div>
      </FundoRifa>
    );
  }

  // ── OFERTA ──
  return (
    <PassoOferta
      talao={talao}
      quantidade={quantidade}
      aoMudarQuantidade={setQuantidade}
      nome={nome}
      aoMudarNome={setNome}
      telefone={telefone}
      aoMudarTelefone={(v) => setTelefone(mascaraTelefone(v))}
      erro={erroPix}
      gerando={gerando}
      aoPagar={pagarComPix}
      imagemFundo={imagemFundo}
    />
  );
}